/**
 * Workspace metadata client for tenant-service `v1/dashboard/tenant`.
 * Same shape as `lib/api/tokens.ts` — `VITE_API_URL` base, Supabase JWT
 * bearer.
 *
 * Deliberately not part of `lib/api/billing.ts`: tier is infrastructure
 * placement, not billing state, and routing the shell's only fetch
 * through the billing client is what put a live payment-provider read
 * in front of every page load.
 */
import { getAuthHeaders } from "./auth-headers";

import { TENANT_TIER } from "@/lib/tenant";
import type { TenantInfo } from "@/types/tenant";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";
const API_BASE = `${API_BASE_URL}/v1/dashboard/tenant`;

/**
 * Carries the status and the problem `code`, because callers have to
 * tell a verdict from a blip: 403 `AUTH-MISSING-TENANT` is permanent for
 * this JWT and retrying it can only fail again, while a 502 is worth
 * another attempt. A bare `Error` collapses the two, which is how a
 * non-retryable 403 came to be re-fired on every authenticated page
 * load and surfaced behind a "Try again" that could never succeed.
 *
 * Mirrors `BillingApiError` rather than sharing it: the two collapse
 * into one type when `auth-headers.ts`'s "an error type callers can
 * branch on" lands across every client at once.
 */
export class TenantApiError extends Error {
  readonly code: string | null;
  readonly status: number;

  constructor(message: string, code: string | null, status: number) {
    super(message);
    this.name = "TenantApiError";
    this.code = code;
    this.status = status;
  }
}

interface ProblemDetail {
  title?: string;
  detail?: string;
  code?: string;
}

const TIERS: readonly string[] = Object.values(TENANT_TIER);

/**
 * The backend pins `tier` to a closed enum in its response schema; this
 * is where that guarantee would otherwise be discarded. A 200 carrying
 * an interstitial, an empty object, or a tier this build predates
 * leaves `tier` undefined — and `undefined !== "dedicated"`, so the
 * page would hand a dedicated-tier workspace the entire self-serve
 * billing UI instead of NotFound. Failing the read is the safe verdict.
 */
function isTenantInfo(value: unknown): value is Pick<TenantInfo, "tier"> {
  return (
    typeof value === "object" &&
    value !== null &&
    TIERS.includes((value as { tier?: unknown }).tier as string)
  );
}

export async function getTenant(): Promise<TenantInfo> {
  const headers = await getAuthHeaders(false);
  const response = await fetch(API_BASE, { headers });

  if (!response.ok) {
    const problem = (await response
      .json()
      .catch(() => ({}) as ProblemDetail)) as ProblemDetail;
    throw new TenantApiError(
      problem.detail || problem.title || "Failed to load workspace",
      problem.code ?? null,
      response.status,
    );
  }

  const body: unknown = await response.json();
  if (!isTenantInfo(body)) {
    throw new TenantApiError("Unexpected workspace response", null, 200);
  }
  return {
    tier: body.tier,
    // Anything but `true` hides Connected apps, including a tenant-service
    // that predates the field: the dashboard and tenant-service deploy
    // separately, and a missing field must not fail the shell's only read.
    mcpEnabled: (body as { mcpEnabled?: unknown }).mcpEnabled === true,
    // The same rule for the Alerting pages.
    alertingEnabled:
      (body as { alertingEnabled?: unknown }).alertingEnabled === true,
  };
}
